// react
import React, { useEffect, useRef } from "react";
import { Prompt } from "react-router-dom";
// redux
import { useAppDispatch } from "../../redux/hooks";
import { setBusy, clearBusy, setDirty, clearDirty } from "../../shell/shellSlice";
// api
import { StatusMessagesDto } from "../../api/Client";
import { CallResultError, ErrorTypeEnum } from "../../api/callHelper";
// common
import StatusMessagesAlertsView from "../alerts/StatusMessagesAlertsView";
import ConflictView from "../alerts/ConflictAlertView";
import * as toastHelper from "../toastHelper";

interface IProps {
    isDirty?: boolean | undefined;
    isBusy?: boolean | undefined;
    error?: CallResultError | undefined;
    statusMessages?: StatusMessagesDto | undefined;
    statusMessagesScrollToTop?: boolean | undefined;
    children: React.ReactNode;
    className?: string | undefined;
}

export default function TaskLayout(props: IProps): JSX.Element {
    const isConflict = props.error && props.error.errorType === ErrorTypeEnum.Conflict409;

    useEffect(() => {
        if (props.error) {
            showCallResultErrorNotification(props.error.errorType, props.error.unitOfWorkCorrelationId);
        }
    }, [props.error]);

    return (
        <>
            <BusyState isBusy={props.isBusy} />
            <DirtyState isDirty={props.isDirty} />
            <div className={props.className}>
                {isConflict && <ConflictView isConflict={isConflict} />}
                {props.statusMessages && <StatusMessagesAlertsView statusMessages={props.statusMessages} scrollWindowToTop={props.statusMessagesScrollToTop} />}
                {props.children}
            </div>
        </>
    );
}

function BusyState(props: { isBusy: boolean | undefined }): JSX.Element {
    const isBusy = props.isBusy;
    const isBusyState = useRef<boolean>(isBusy ?? false);
    const dispatch = useAppDispatch();

    useEffect(() => {
        // when wired up, can only be true/false, undefined means this feature is not being used
        if (isBusy === undefined) return;

        // nothing has changed
        if (isBusy === isBusyState.current) return;

        // changed: flip the shell's busy state
        isBusyState.current = isBusy;
        if (isBusy!) {
            dispatch(setBusy());
        } else {
            dispatch(clearBusy());
        }
    }, [isBusy, dispatch]);

    useEffect(() => {
        // clear on unmount
        return () => {
            if (isBusyState.current) {
                dispatch(clearBusy());
            }
        };
    }, [dispatch]);

    return <></>;
}

function DirtyState(props: { isDirty: boolean | undefined }): JSX.Element {
    const message = "There are unsaved changes. Are you sure you wish to continue?";
    const isDirty = props.isDirty;
    const isDirtyState = useRef<boolean>(isDirty ?? false);
    const dispatch = useAppDispatch();

    useEffect(() => {
        // when wired up, can only be true/false, undefined means this feature is not being used
        if (isDirty === undefined) return;

        // nothing has changed
        if (isDirty === isDirtyState.current) return;

        // changed: flip the shell's dirty state
        isDirtyState.current = isDirty;
        if (isDirty) {
            dispatch(setDirty());
        } else {
            dispatch(clearDirty());
        }
    }, [isDirty, dispatch]);

    useEffect(() => {
        // clear on unmount
        return () => {
            if (isDirtyState.current) {
                dispatch(clearDirty());
            }
        };
    }, [dispatch]);

    // NOTE prompt is triggered by undefined, check if this is resolved when patching react-router-dom
    if (isDirty === undefined) return <></>;

    return <Prompt when={isDirty} message={message} />;
}

function showCallResultErrorNotification(error: ErrorTypeEnum, correlationId: string) {
    let message = "Please try the operation again. If the issue persists, please contact support.";
    if (correlationId && correlationId.length > 0) {
        message = `Please try the operation again. If the issue persists, please contact support and quote ${correlationId.substring(0, 6).toUpperCase()}.`;
    }

    switch (error) {
        case ErrorTypeEnum.ServiceUnavailable:
            toastHelper.showExceptionNotification("Service Unavailable", message);
            return;
        case ErrorTypeEnum.InternalServerError500:
            toastHelper.showExceptionNotification("Internal Error", message);
            return;
        case ErrorTypeEnum.NotAuthenticated401:
            toastHelper.showExceptionNotification("Not Authenticated", message);
            return;
        case ErrorTypeEnum.NotAuthorised403:
            toastHelper.showExceptionNotification("Not Authorised", message);
            return;
        case ErrorTypeEnum.BadRequest400:
            toastHelper.showExceptionNotification("Bad Request", message);
            return;
        case ErrorTypeEnum.NotFound404:
            toastHelper.showExceptionNotification("Not Found", message);
            return;
        case ErrorTypeEnum.Conflict409:
            toastHelper.showExceptionNotification("Conflict", message);
            return;
        case ErrorTypeEnum.ServerTooBusy429:
            toastHelper.showExceptionNotification("Server Too Busy", message);
            return;
        case ErrorTypeEnum.UnexpectedError:
            toastHelper.showExceptionNotification("Unexpected Error", message);
            return;
    }
}
